/**
 * Finans presenter'i
 * Finans ekraninin render ve form etkileşimlerini yonetir.
 */

import { doc, updateDoc, db, COLLECTIONS } from '../../firebase-config.js';
import { AppState } from '../../modules/state.js';
import { FirebaseService } from '../../shared/services/firebase.service.js';
import { CATEGORY_LABELS } from '../../app.config.js';
import { formatDate, formatNumber } from '../../shared/utils/formatters.js';

interface FinancePresenterDeps {
    openModal: (modalId: string) => void;
    showToast: (message: string, type?: string) => void;
    generateId: () => string;
    escapeHtml: (value: string) => string;
}

function findTransactionIndex(transactionKey: string) {
    const normalizedKey = String(transactionKey || '').trim();
    return AppState.transactions.findIndex(t =>
        String(t.id || '').trim() === normalizedKey ||
        String(t._docId || '').trim() === normalizedKey
    );
}

function getSelectedTransactionFilters(): { typeFilter: string; yearFilter: number } {
    const typeFilter = (document.getElementById('transaction-type-filter') as HTMLSelectElement | null)?.value || 'all';
    const currentYear = new Date().getFullYear();
    const selectedYear = parseInt((document.getElementById('transaction-year-filter') as HTMLSelectElement | null)?.value || String(currentYear), 10);

    return {
        typeFilter,
        yearFilter: Number.isFinite(selectedYear) ? selectedYear : currentYear
    };
}

function getFilteredTransactions() {
    const { typeFilter, yearFilter } = getSelectedTransactionFilters();

    return AppState.transactions.filter(t => {
        const date = new Date(t.date);
        if (date.getFullYear() !== yearFilter) return false;
        if (typeFilter !== 'all' && t.type !== typeFilter) return false;
        return true;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

function escapeExcelCell(value: unknown): string {
    const text = String(value ?? '');
    const safeText = /^[=+\-@]/.test(text.trimStart()) ? `'${text}` : text;

    return safeText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function downloadExcelHtml(filename: string, html: string): void {
    const blob = new Blob([`\ufeff${html}`], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export function updateFinanceSummaryPresenter(): void {
    const income = AppState.transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
    const expense = AppState.transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
    const balance = income - expense;

    document.getElementById('admin-total-income')!.textContent = `₺${formatNumber(income)}`;
    document.getElementById('admin-total-expense')!.textContent = `₺${formatNumber(expense)}`;
    const balanceEl = document.getElementById('admin-net-balance')!;
    balanceEl.textContent = `₺${formatNumber(balance)}`;
    balanceEl.className = `summary-value ${balance >= 0 ? 'success' : 'warning'}`;
}

export function renderTransactionsPresenter(deps: FinancePresenterDeps): void {
    const container = document.getElementById('transactions-list');
    if (!container) return;

    const { typeFilter, yearFilter } = getSelectedTransactionFilters();
    const activeFilters = document.getElementById('transaction-active-filters');

    if (activeFilters) {
        activeFilters.innerHTML = [
            `<span class="active-filter-chip">Yıl: ${yearFilter}</span>`,
            `<span class="active-filter-chip">Tür: ${typeFilter === 'all' ? 'Tümü' : typeFilter === 'income' ? 'Gelir' : 'Gider'}</span>`
        ].join('');
    }

    const filtered = getFilteredTransactions();

    if (filtered.length === 0) {
        const availableYears = [...new Set(
            AppState.transactions
                .map(t => new Date(t.date).getFullYear())
                .filter(year => Number.isFinite(year))
        )].sort((a, b) => b - a);

        const yearHint = availableYears.length
            ? `<p class="empty-state-hint">Mevcut kayıt yılları: ${availableYears.join(', ')}</p>`
            : '';

        container.innerHTML = `
            <div class="empty-state">
                <p>Bu kriterlere uygun kayıt bulunamadı</p>
                ${yearHint}
                <button type="button" class="btn btn-primary btn-sm empty-action-btn" id="empty-add-transaction-btn">+ İlk Kaydı Ekle</button>
            </div>
        `;

        document.getElementById('empty-add-transaction-btn')?.addEventListener('click', () => {
            document.getElementById('transaction-modal-title')!.textContent = 'Gelir/Gider Ekle';
            (document.getElementById('transaction-id') as HTMLInputElement).value = '';
            (document.getElementById('transaction-date') as HTMLInputElement).value = new Date().toISOString().split('T')[0];
            deps.openModal('transaction-modal');
        });
        return;
    }

    const categoryIcons: Record<string, string> = {
        aidat: '🏠',
        kira: '🔑',
        diger_gelir: '💵',
        elektrik: '⚡',
        su: '💧',
        dogalgaz: '🔥',
        temizlik: '🧹',
        bakim: '🔧',
        guvenlik: '🛡️',
        sigorta: '📋',
        diger_gider: '📦'
    };

    container.innerHTML = filtered.map(transaction => `
        <div class="transaction-card ${transaction.type}">
            <div class="transaction-icon">
                ${categoryIcons[transaction.category] || (transaction.type === 'income' ? '💰' : '💸')}
            </div>
            <div class="transaction-info">
                <div class="transaction-category">${CATEGORY_LABELS[transaction.category] || transaction.category}</div>
                <div class="transaction-description">${deps.escapeHtml(transaction.description || '-')}</div>
            </div>
            <div class="transaction-meta">
                <div class="transaction-amount ${transaction.type}">${transaction.type === 'income' ? '+' : '-'}₺${formatNumber(transaction.amount)}</div>
                <div class="transaction-date">📅 ${formatDate(transaction.date)}</div>
            </div>
            <div class="transaction-actions">
                <button class="btn btn-secondary btn-sm" onclick="editTransaction('${transaction._docId || transaction.id}')" title="Düzenle">✏️</button>
                <button class="btn btn-danger btn-sm" onclick="deleteTransaction('${transaction._docId || transaction.id}')" title="Sil">🗑️</button>
            </div>
        </div>
    `).join('');
}

export function exportTransactionsToExcelPresenter(deps: FinancePresenterDeps): void {
    const { typeFilter, yearFilter } = getSelectedTransactionFilters();
    const transactions = getFilteredTransactions();

    if (transactions.length === 0) {
        deps.showToast('Excel aktarımı için uygun kayıt bulunamadı.', 'warning');
        return;
    }

    const totalIncome = transactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    const totalExpense = transactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
    const filterLabel = typeFilter === 'all' ? 'Tüm Kayıtlar' : typeFilter === 'income' ? 'Gelir' : 'Gider';
    const exportedAt = new Date().toLocaleString('tr-TR');
    const rows = transactions.map(transaction => {
        const amount = Number(transaction.amount) || 0;

        return `
            <tr>
                <td>${escapeExcelCell(formatDate(transaction.date))}</td>
                <td>${escapeExcelCell(transaction.type === 'income' ? 'Gelir' : 'Gider')}</td>
                <td>${escapeExcelCell(CATEGORY_LABELS[transaction.category] || transaction.category)}</td>
                <td style="mso-number-format:'0.00'">${amount.toFixed(2)}</td>
                <td>${escapeExcelCell(transaction.description || '-')}</td>
            </tr>
        `;
    }).join('');

    const html = `
        <html>
            <head>
                <meta charset="utf-8">
                <style>
                    table { border-collapse: collapse; }
                    th, td { border: 1px solid #d9e2ec; padding: 8px; }
                    th { background: #eef2f7; font-weight: 700; }
                    .summary-label { font-weight: 700; }
                </style>
            </head>
            <body>
                <table>
                    <tr><td class="summary-label">Rapor</td><td>Gelir/Gider</td></tr>
                    <tr><td class="summary-label">Yıl</td><td>${yearFilter}</td></tr>
                    <tr><td class="summary-label">Tür</td><td>${escapeExcelCell(filterLabel)}</td></tr>
                    <tr><td class="summary-label">Aktarım Tarihi</td><td>${escapeExcelCell(exportedAt)}</td></tr>
                    <tr><td class="summary-label">Toplam Gelir</td><td style="mso-number-format:'0.00'">${totalIncome.toFixed(2)}</td></tr>
                    <tr><td class="summary-label">Toplam Gider</td><td style="mso-number-format:'0.00'">${totalExpense.toFixed(2)}</td></tr>
                    <tr><td class="summary-label">Net Bakiye</td><td style="mso-number-format:'0.00'">${(totalIncome - totalExpense).toFixed(2)}</td></tr>
                </table>
                <br>
                <table>
                    <thead>
                        <tr>
                            <th>Tarih</th>
                            <th>Tür</th>
                            <th>Kategori</th>
                            <th>Tutar (TL)</th>
                            <th>Açıklama</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </body>
        </html>
    `;
    const typeSlug = typeFilter === 'all' ? 'tum-kayitlar' : typeFilter === 'income' ? 'gelir' : 'gider';

    downloadExcelHtml(`gelir-gider-${yearFilter}-${typeSlug}.xls`, html);
    deps.showToast('Gelir/gider kayıtları Excel dosyası olarak indirildi.', 'success');
}

export async function addTransactionPresenter(data: Record<string, any>, deps: FinancePresenterDeps): Promise<boolean> {
    const transactionId = deps.generateId();
    const transaction = { id: transactionId, _docId: transactionId, ...data, createdAt: new Date().toISOString() };
    AppState.transactions.push(transaction);

    try {
        const savedId = await FirebaseService.add(COLLECTIONS.TRANSACTIONS, transaction);
        if (savedId && savedId !== transactionId) {
            transaction._docId = savedId;
        }
        deps.showToast('Kayıt eklendi', 'success');
        return true;
    } catch (error: any) {
        AppState.transactions = AppState.transactions.filter(t => (t._docId || t.id) !== transactionId);
        console.error('Transaction add error:', error);

        if (error?.code === 'permission-denied' || error?.code === 'firestore/permission-denied') {
            deps.showToast('Kayıt yetki nedeniyle eklenemedi. Lütfen yönetici olarak tekrar giriş yapın.', 'error');
        } else {
            deps.showToast('Kayıt eklenemedi. Lütfen tekrar deneyin.', 'error');
        }
        return false;
    }
}

export async function updateTransactionPresenter(id: string, data: Record<string, any>, deps: FinancePresenterDeps): Promise<boolean> {
    const index = findTransactionIndex(id);
    if (index === -1) {
        return false;
    }

    const current = AppState.transactions[index];
    const transactionDocId = String(current._docId || current.id || id || '').trim();
    const transactionBusinessId = String(current.id || current._docId || id || '').trim();

    if (!transactionDocId) {
        deps.showToast('Kayıt kimliği bulunamadı. Sayfayı yenileyip tekrar deneyin.', 'error');
        return false;
    }

    const updated = {
        ...current,
        ...data,
        id: transactionBusinessId,
        _docId: transactionDocId
    };
    AppState.transactions[index] = updated;

    try {
        await updateDoc(doc(db, COLLECTIONS.TRANSACTIONS, transactionDocId), {
            id: transactionBusinessId,
            type: updated.type,
            category: updated.category,
            amount: Number(updated.amount) || 0,
            date: updated.date,
            description: (updated.description || '').toString(),
            createdAt: updated.createdAt || new Date().toISOString(),
            isAutoGenerated: !!updated.isAutoGenerated
        });
        deps.showToast('Kayıt güncellendi', 'success');
        return true;
    } catch (error: any) {
        AppState.transactions[index] = current;
        console.error('Transaction update error:', error);

        if (error?.code === 'permission-denied' || error?.code === 'firestore/permission-denied') {
            deps.showToast('İşlem yetkisi doğrulanamadı. Lütfen yönetici olarak tekrar giriş yapın.', 'error');
        } else {
            deps.showToast('Kayıt güncellenemedi. Lütfen tekrar deneyin.', 'error');
        }
        return false;
    }
}

export function deleteTransactionPresenter(id: string, deps: FinancePresenterDeps): void {
    if (!confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;

    const index = findTransactionIndex(id);
    const transaction = index >= 0 ? AppState.transactions[index] : null;
    const transactionDocId = transaction?._docId || id;
    AppState.transactions = AppState.transactions.filter(t =>
        String(t.id || '').trim() !== String(id || '').trim() &&
        String(t._docId || '').trim() !== String(id || '').trim()
    );
    FirebaseService.delete(COLLECTIONS.TRANSACTIONS, transactionDocId);
    renderTransactionsPresenter(deps);
    updateFinanceSummaryPresenter();
    deps.showToast('Kayıt silindi', 'success');
}

export function editTransactionPresenter(id: string, deps: FinancePresenterDeps): void {
    const index = findTransactionIndex(id);
    const transaction = index >= 0 ? AppState.transactions[index] : null;
    if (!transaction) return;

    document.getElementById('transaction-modal-title')!.textContent = 'Kayıt Düzenle';
    (document.getElementById('transaction-id') as HTMLInputElement).value = transaction._docId || transaction.id;
    (document.getElementById('transaction-type') as HTMLSelectElement).value = transaction.type;
    (document.getElementById('transaction-category') as HTMLSelectElement).value = transaction.category;
    (document.getElementById('transaction-amount') as HTMLInputElement).value = transaction.amount.toString();
    (document.getElementById('transaction-date') as HTMLInputElement).value = transaction.date;
    (document.getElementById('transaction-description') as HTMLTextAreaElement).value = transaction.description || '';
    deps.openModal('transaction-modal');
}
